import { useState } from 'react'
import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  AddGroupFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddGroupFormData
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderSelectControl,
  renderTextareaControl,
  renderButtonControl,
  type ControlConfig
} from '../../formRenderFunctions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { X } from 'lucide-react'
import { LiveUriPreview } from '@/components/rema/shared'
import { RemaEnvironmentBar } from '@/components/rema/RemaEnvironmentBar'
import { buildGroupUri, type RemaEnvironment } from '@/hooks/useRemaUriBuilder'
import { AppLogger } from '@/lib/logger'

const log = AppLogger.getLogger(import.meta)

export type AddGroupComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddGroupFormData>
  showHeader?: boolean
  submitLabel?: string
}

const validateField = (name: keyof AddGroupFormData, value: unknown): string | null => {
  const shape = AddGroupFormSchema.shape[name]
  if (!shape) return null
  const parsed = shape.safeParse(value)
  if (!parsed.success) return parsed.error.issues[0]?.message ?? 'Invalid value'
  return null
}

// Group names must be lowercase (they form part of the group URI).
const validateLowercaseName = (value: unknown): string | null => {
  if (typeof value === 'string' && value.trim() !== '' && value !== value.toLowerCase()) {
    return 'Name must be in lowercase letters only'
  }
  return null
}

type SelectOption = { value: string; label: string }

// String-keyed searchable multi-select (mirrors OrderedMultiSelect's UI without
// ordering — roles/members are unordered string identifiers).
function StringMultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Search and add...',
}: {
  options: SelectOption[]
  value: string[]
  onChange: (next: string[]) => void
  placeholder?: string
}) {
  const [search, setSearch] = useState('')

  const selectedOptions = value.map((v) => options.find((o) => o.value === v) ?? { value: v, label: v })
  const availableOptions = options.filter(
    (o) => !value.includes(o.value) && o.label.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-2">
      <div className="relative">
        <Input
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {search && availableOptions.length > 0 && (
          <div className="absolute z-10 top-full mt-1 w-full rounded-md border bg-popover shadow-md max-h-48 overflow-auto">
            {availableOptions.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground"
                onClick={() => { onChange([...value, opt.value]); setSearch('') }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedOptions.length > 0 && (
        <div className="space-y-1">
          {selectedOptions.map((opt, idx) => (
            <div key={opt.value} className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-1.5">
              <span className="flex-1 text-sm">{opt.label}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-destructive hover:text-destructive"
                onClick={() => { const next = [...value]; next.splice(idx, 1); onChange(next) }}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function AddGroupComposer(props: AddGroupComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props

  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const [environment, setEnvironment] = useState<RemaEnvironment>('development')
  const [roles, setRoles] = useState<string[]>(initialValues?.roles ?? [])
  const [members, setMembers] = useState<string[]>(initialValues?.members ?? [])

  const isRequiredField = (name: string) => {
    return ['namespace', 'organization', 'groupName'].includes(name)
  }

  const { data: rolesData } = useApiQuery('getRoles', {})
  const { data: usersData } = useApiQuery('getUsers', {})

  const roleOptions: SelectOption[] = ((rolesData as any)?.content?.roles ?? (rolesData as any)?.roles ?? []).map((r: any) => ({
    value: r.roleUri ?? `${r.namespace}://role/${r.roleType}/${r.organization}/${r.roleName}`,
    label: r.roleName ?? r.roleUri,
  }))

  const memberOptions: SelectOption[] = ((usersData as any)?.content?.users ?? (usersData as any)?.users ?? []).map((u: any) => ({
    value: String(u.userNumber),
    label: `${u.firstName ?? ''} ${u.lastName ?? ''} (${u.emailAddress ?? ''})`.trim(),
  }))

  const upsertGroup = useApiMutation('upsertGroup', 'POST', {
    invalidates: ['getGroups'],
    onSuccess: () => onSuccess?.(),
    onError: (error) => {
      log.error('Failed to create group:', error)
      onError?.(error)
    },
  })

  const form = useAppForm({
    schema: AddGroupFormSchema,
    defaultValues: {
      groupNumber: initialValues?.groupNumber,
      namespace: initialValues?.namespace ?? ('' as any),
      organization: initialValues?.organization ?? '',
      groupName: initialValues?.groupName ?? '',
      groupDescription: initialValues?.groupDescription ?? '',
      roles: initialValues?.roles ?? [],
      members: initialValues?.members ?? [],
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddGroupFormData
      const parsed = AddGroupFormSchema.safeParse({
        groupNumber: initialValues?.groupNumber,
        namespace: formValue.namespace,
        organization: formValue.organization?.trim() ?? '',
        groupName: formValue.groupName?.trim() ?? '',
        groupDescription: formValue.groupDescription?.trim() ?? '',
        roles,
        members,
      })

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as any, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        return
      }

      // Enforce lowercase group name before submit.
      const nameCaseError = validateLowercaseName(parsed.data.groupName)
      if (nameCaseError) {
        form.setFieldMeta('groupName', (meta) => ({ ...meta, isTouched: true, errors: [nameCaseError] }))
        return
      }

      await upsertGroup.mutateAsync({ ...transformToApiRequest(parsed.data), groupUri: liveUri })
    },
  })

  const formState = form.state.values as any
  const liveUri = buildGroupUri({
    namespace: formState.namespace ?? '',
    organization: formState.organization ?? '',
    groupName: formState.groupName ?? '',
  })

  const renderControl = (control: any) => {
    const isRequired = isRequiredField(control.name)
    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          // Flag uppercase on the group name the moment it's typed.
          onChange: ({ value }) =>
            control.name === 'groupName' ? validateLowercaseName(value) || undefined : undefined,
          onBlur: ({ value }) =>
            validateField(control.name as keyof AddGroupFormData, value)
            || (control.name === 'groupName' ? validateLowercaseName(value) : null)
            || undefined,
        }}
        listeners={
          control.name === 'groupName'
            ? {
                // Mark touched on change so the lowercase error shows while typing.
                onChange: () => form.setFieldMeta('groupName', (m) => ({ ...m, isTouched: true })),
              }
            : undefined
        }
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
          }
          if (control.htmlControl === 'textarea') return renderTextareaControl(config, bound)
          if (control.htmlControl === 'select') {
            return renderSelectControl({
              ...config,
              options: control.selectOptions?.map((o: any) => ({ value: o.value, label: o.label })) || [],
            }, bound)
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  return (
    <form.AppForm>
      <div className="space-y-6">
        {showHeader && (
          <div className="space-y-1">
            <h2 className="text-lg font-semibold">{FORM_CONFIG.formTitle}</h2>
            <p className="text-sm text-muted-foreground">{FORM_CONFIG.formDescription}</p>
          </div>
        )}

        <RemaEnvironmentBar
          environment={environment}
          onEnvironmentChange={setEnvironment}
          namespace={formState.namespace}
        />

        {groups.map((group) => {
          const controls = controlsByGroup.get(group.formGroupName) || []
          if (!controls.length) return null
          return (
            <div key={group.formGroupName} className="space-y-4">
              {controls.map(renderControl)}
            </div>
          )
        })}

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Roles</Label>
          <StringMultiSelect
            options={roleOptions}
            value={roles}
            onChange={setRoles}
            placeholder="Search and add roles..."
          />
        </div>

        <div className="space-y-1.5">
          <Label className="text-sm font-medium">Members</Label>
          <StringMultiSelect
            options={memberOptions}
            value={members}
            onChange={setMembers}
            placeholder="Search and add members..."
          />
        </div>

        <LiveUriPreview uri={liveUri} label="Group URI Preview" />

        <div className="pt-4 flex justify-center gap-3">
          {renderButtonControl({
            label: FORM_CONFIG.cancelButton?.label ?? '',
            variant: 'outline',
            onClick: onCancel,
            visibility: !!onCancel,
            type: 'button'
          })}
          {renderButtonControl({
            label: upsertGroup.isPending
              ? (FORM_CONFIG.submitButton?.loadingLabel ?? '')
              : (submitLabel ?? FORM_CONFIG.submitButton?.label ?? ''),
            variant: 'default',
            type: 'submit'
          }, { SubmitButton: form.SubmitButton })}
        </div>
      </div>
    </form.AppForm>
  )
}

export { AddGroupFormSchema, transformToApiRequest, FORM_CONFIG, type AddGroupFormData } from './schema'
